import { useEffect, useState } from "react";
import { api } from "../../api/client";
import { Button } from "../../components/Button";
import { useToast } from "../../components/Toast";
import type { UserPick } from "../../api/types";

// 覆核指派(業主 2026-11-02):每一種覆核除了老闆之外還可以給誰。
// 老闆一律可以(勾著、改不掉)。製單可以核自己開的單;假單被指派的人不能核自己的假。後端:app/routers/reviewers.py。

interface ReviewKind {
  key: string;
  label: string;
  user_ids: number[];
}

interface ReviewerSettingsData {
  kinds: ReviewKind[];
  owner_ids: number[];
}

const PATH = "/settings/reviewers";

export function ReviewerSettings() {
  const toast = useToast();
  const [data, setData] = useState<ReviewerSettingsData | null>(null);
  const [users, setUsers] = useState<UserPick[]>([]);
  const [picked, setPicked] = useState<Record<string, number[]>>({});
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api.get<ReviewerSettingsData>(PATH)
      .then((got) => {
        setData(got);
        setPicked(Object.fromEntries(got.kinds.map((k) => [k.key, k.user_ids])));
      })
      .catch(() => setFailed(true));
    api.get<UserPick[]>("/users/pickable").then((got) => setUsers(Array.isArray(got) ? got : [])).catch(() => undefined);
  }, []);

  if (failed) return <p role="alert" className="m-0 text-sm text-[var(--danger)]">覆核指派載入失敗,請重新整理。</p>;
  if (!data) return null;

  const owners = new Set(data.owner_ids);
  const dirty = data.kinds.some((k) => [...(picked[k.key] ?? [])].sort().join() !== [...k.user_ids].sort().join());

  const toggle = (kind: string, id: number) => {
    setPicked((prev) => {
      const now = prev[kind] ?? [];
      return { ...prev, [kind]: now.includes(id) ? now.filter((x) => x !== id) : [...now, id] };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const got = await api.put<ReviewerSettingsData>(PATH, { assignments: picked });
      setData(got);
      setPicked(Object.fromEntries(got.kinds.map((k) => [k.key, k.user_ids])));
      toast.push("覆核指派已儲存", "success");
    } catch (error) {
      toast.push(error instanceof Error ? error.message : "儲存失敗", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="m-0 text-sm text-[var(--text-2)]">
        老闆一律可以覆核,這裡勾的人也可以。製單可以覆核自己開的單(自己再確認一次);假單核准不能核自己的假。
      </p>
      {data.kinds.map((kind) => (
        <fieldset key={kind.key} className="m-0 flex flex-col gap-1.5 rounded-[var(--radius)] border border-[var(--border)] p-3">
          <legend className="px-1 text-sm font-semibold text-[var(--text)]">{kind.label}</legend>
          {users.map((u) => {
            const isOwner = owners.has(u.id);
            return (
              <label key={u.id} className="flex items-center gap-2 text-sm text-[var(--text)]">
                <input
                  type="checkbox"
                  checked={isOwner || (picked[kind.key] ?? []).includes(u.id)}
                  disabled={isOwner}
                  onChange={() => toggle(kind.key, u.id)}
                />
                {u.name}
                {isOwner ? <span className="text-xs text-[var(--text-3)]">老闆</span> : null}
              </label>
            );
          })}
        </fieldset>
      ))}
      <div>
        <Button type="button" variant="primary" loading={saving} disabled={!dirty} onClick={() => void save()}>
          儲存
        </Button>
      </div>
    </div>
  );
}
